'use strict';

/**
 * Pict-Provider-DeliverableBoardAPI
 * ---------------------------------
 * Browser-side client for the one Deliverable endpoint auto-CRUD cannot serve: the Deliver board
 * dashboard (kanban lanes by stage, with the resolved kit stage labels). Basic Deliverable CRUD goes
 * through the generic EntityAPI; this provider owns the dashboard read and the kanban move. The move
 * is a plain PUT /1.0/Deliverable (Status + SortOrder), exactly like the issue and feature boards, so
 * the server-side status-change audit logs the transition (WorkflowStatusEvent).
 *
 * AppData.DeliverableBoard = { Lanes, Stages, Counts, Loading, Error }
 *   card = { IDDeliverable, DeliverableNumber, Title, Status, CustomerName, ExpectationTitle,
 *            ChildCount, IsChild, SortOrder }
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'DeliverableBoardAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

const _SORT_STEP = 100; // sparse SortOrder so a drop rarely renumbers a lane

class PictProviderDeliverableBoardAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultProviderConfiguration, pOptions || {}), pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	store()
	{
		if (!this.fable.AppData.DeliverableBoard)
		{
			this.fable.AppData.DeliverableBoard =
			{
				Lanes: [], Stages: [], Counts: { Total: 0, Open: 0, Accepted: 0 }, Loading: false, Error: null
			};
		}
		return this.fable.AppData.DeliverableBoard;
	}

	_url(pPath) { let tmpPrefix = this.routePrefix; if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; } return tmpPrefix + pPath.replace(/^\//, ''); }
	// Recovery-covered read; preserves the null-on-error contract callers rely on.
	_get(pPath) { return libFrameworkClient.getJSON(this.fable, this._url(pPath)).catch(() => null); }

	// The whole Deliver board in one read; mirrored into AppData.DeliverableBoard.
	dashboard()
	{
		let tmpStore = this.store();
		tmpStore.Loading = true;
		return this._get('Deliverables/Dashboard').then((pBody) =>
		{
			if (pBody)
			{
				tmpStore.Lanes = pBody.Lanes || [];
				tmpStore.Stages = pBody.Stages || [];
				tmpStore.Counts = pBody.Counts || tmpStore.Counts;
				tmpStore.Error = null;
			}
			else { tmpStore.Error = 'Could not load the deliver board.'; }
			tmpStore.Loading = false;
			return tmpStore;
		}).catch((pErr) => { tmpStore.Loading = false; tmpStore.Error = (pErr && pErr.message) || 'load failed'; return tmpStore; });
	}

	// Next sparse SortOrder for appending a card to the end of a lane.
	nextSortOrder(pLaneKey)
	{
		let tmpStore = this.store();
		let tmpMax = 0;
		for (let i = 0; i < tmpStore.Lanes.length; i++)
		{
			if (tmpStore.Lanes[i].Key !== pLaneKey) { continue; }
			let tmpCards = tmpStore.Lanes[i].Cards || [];
			for (let j = 0; j < tmpCards.length; j++) { if ((tmpCards[j].SortOrder || 0) > tmpMax) { tmpMax = tmpCards[j].SortOrder; } }
		}
		return tmpMax + _SORT_STEP;
	}

	// Move a deliverable to a stage: PUT the Status + SortOrder, so the server audit logs the transition.
	moveCard(pID, pToStatus, pSortOrder)
	{
		let tmpBody = { IDDeliverable: pID, Status: pToStatus, SortOrder: pSortOrder };
		// EntityProvider.updateEntity -> the shared RestClient's putJSON (recovery-covered).
		return libFrameworkClient.updateEntity(this.fable, 'Deliverable', tmpBody);
	}
}

module.exports = PictProviderDeliverableBoardAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
